/**
 * HS256 is HMAC-SHA256, which `crypto.subtle` does natively.
 *
 * Do not reach for `jsonwebtoken` or a similar library here. They sign through Node's `crypto`, which
 * the `fallback` in `config-overrides.js` satisfies with `crypto-browserify` — `elliptic`, `bn.js`,
 * `public-encrypt` and `diffie-hellman` then enter the bundle every user downloads, for an algorithm
 * none of them serve. That cascade is the cost, not the library, so a smaller JWT library avoids none
 * of it.
 *
 * Requires a secure context: `crypto.subtle` is undefined over plain http on a non-localhost host.
 */

const HS256_HEADER = { alg: 'HS256', typ: 'JWT' } as const;

const encoder = new TextEncoder();

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  // Spreading into String.fromCharCode overflows the call stack once the input is a few tens of KB.
  bytes.forEach((byte) => (binary += String.fromCharCode(byte)));

  return btoa(binary).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

function encodeSegment(value: object): string {
  return base64Url(encoder.encode(JSON.stringify(value)));
}

export interface ISignJwtHs256Options {
  /**
   * @description Extra header claims, merged under `{ alg: 'HS256', typ: 'JWT' }`. `kid` is the usual
   * reason to pass one. `alg` and `typ` are fixed, because a header claiming anything else would
   * describe a signature this function did not produce.
   *
   * `Omit` cannot express that: `keyof Record<string, T>` is `string`, so `Exclude` removes nothing.
   */
  header?: { alg?: never; typ?: never; [claim: string]: string | undefined };

  /**
   * @description Sets `exp` to `iat + expiresInSeconds`. Omit to sign a token with no expiry.
   */
  expiresInSeconds?: number;
}

/**
 * @name signJwtHs256
 *
 * @description Signs a JWT with HMAC-SHA256.
 *
 * Header and claim property order is fixed: `jwtUtils.test.ts` asserts exact token bytes against
 * frozen reference tokens, so reordering fails those assertions. Nothing at runtime depends on it.
 */
export async function signJwtHs256(
  claims: Record<string, unknown>,
  secret: string,
  { header, expiresInSeconds }: ISignJwtHs256Options = {}
): Promise<string> {
  const issuedAt = Math.floor(Date.now() / 1000);
  const body = {
    ...claims,
    iat: issuedAt,
    ...(expiresInSeconds === undefined ? {} : { exp: issuedAt + expiresInSeconds }),
  };

  const signingInput = `${encodeSegment({ ...HS256_HEADER, ...header })}.${encodeSegment(body)}`;

  const key = await crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(signingInput));

  return `${signingInput}.${base64Url(new Uint8Array(signature))}`;
}
